import React, { Component } from "react";
import { connect } from "react-redux";
import {
    COLORS,
    DISTRIBUTOR_COOPERATION_TYPES,
    DISTRIBUTOR_STATUSES,
    DISTRIBUTOR_TERMINAL_TYPES,
    PAYMENT_TYPES,
} from "../../../config/constants";
import Select from "react-select";
import translator from "../../localization/translator";
import "./InputsStyle.css";
import moment from "moment";
import { convertEtaToMinutes } from "../../../lib/utils";
import { auto } from "async";
import { getNearestDistributorList } from "../../distributors/distributors.actions";

class SelectList extends Component {
    getSelectedLabel = (distributor) => {
        if (!distributor) return "";
        const letter = distributor.vehicleLetter;
        const rawName = distributor.name || "";
        const hasPrefixedName = /^\([^)]+\)\s+/.test(rawName);
        const effectiveLetter = letter || (hasPrefixedName ? rawName.match(/^\(([^)]+)\)/)?.[1] : "");
        const displayName = hasPrefixedName && !letter ? rawName.replace(/^\([^)]+\)\s+/, "") : rawName;
        if (effectiveLetter) {
            return `${effectiveLetter} ${displayName}`.trim();
        }
        return displayName;
    };

    formatOptionLabel = (option, { context } = {}) => {
        const label = option?.label || "";
        if (typeof label !== "string") return label;
        // For selected value, show same info as map popup (without payableAmount) to match
        let displayLabel = label;
        if (context === "value") {
            const stripPayable = label.match(/^\s*\d+\s+(\([^)]+\)\s*.*)$/);
            if (stripPayable) displayLabel = stripPayable[1];
        }
        const match = displayLabel.match(/^(\s*\d*\s*)(\([^)]+\))\s*(.*)$/);
        if (match) {
            const leading = match[1] || "";
            const prefix = match[2];
            const rest = match[3] || "";
            return (
                <span title={label}>
                    {leading}
                    <span>{prefix}</span>
                    {rest ? ` ${rest}` : ""}
                </span>
            );
        }
        return <span title={label}>{displayLabel}</span>;
    };

    state = {
        selectedDistributor: {
            label: this.getSelectedLabel(this.props.defaultDistributor),
            value: this.props.defaultDistributor?._id || "",
        },
        nearestList: [],
        screenWidth: typeof window !== "undefined" ? window.innerWidth : 0,
        menuPlacement: "bottom",
    };
    distributors = [];
    menuIsOpen = this.props.menuIsOpen;
    handleResize = this.handleResize.bind(this);

    selectRef = React.createRef();

    getMenuPlacement = () => {
        const controlRef = this.selectRef?.current?.select?.controlRef;

        if (!controlRef) {
            return "bottom";
        }

        const rect = controlRef.getBoundingClientRect();

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        return spaceBelow < 300 && spaceAbove > spaceBelow
            ? "top"
            : "bottom";
    };

    selectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 99999,
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: 1,
            cursor: "pointer",
        }),
        control: (provided) => ({
            ...provided,
            width: this.props.inputWidth,
            direction: "ltr",
            position: "relative",
            overflow: "hidden",
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
        }),
        valueContainer: (provided) => ({
            ...provided,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            flexWrap: "nowrap",
        }),
        menu: (provided) => {
            const controlRef = this.selectRef?.current?.select?.controlRef;

            if (!controlRef) {
                return provided;
            }

            const rect = controlRef.getBoundingClientRect();

            const estimatedMenuWidth = 500;

            const overflowRight =
                rect.left + estimatedMenuWidth - window.innerWidth;

            return {
                ...provided,
                width: "auto",
                minWidth: "200px",
                whiteSpace: "nowrap",
                left: this.props.fromMap ? overflowRight > 0 ? -overflowRight - 10 : 0 : -estimatedMenuWidth + 400,
            };
        },
        menuList: (provided) => ({
            ...provided,
            ...(this.props.fromMap && { maxHeight: 600 }),
            overflowY: "auto",
        }),
        option: (styles, { data, isFocused }) => {
            let backgroundColor,
                borderLeft,
                color = COLORS.BLACK;
            if (data && data.className) {
                if (data.className.includes("text-red")) {
                    color = COLORS.RED;
                }
                if (data.className.includes("border-left-active")) {
                    borderLeft = "10px solid #fdff32 !important";
                }
                if (data.className.includes("unavailable-bg")) {
                    backgroundColor = COLORS.GREY;
                } else if (data.className.includes("success-bg")) {
                    backgroundColor = COLORS.GREEN;
                } else if (data.className.includes("inplace-bg")) {
                    backgroundColor = COLORS.BLUE;
                } else if (data.className.includes("accepted-bg")) {
                    backgroundColor = COLORS.ORANGE;
                } else if (data.className.includes("assigned-bg")) {
                    backgroundColor = COLORS.YELLOW;
                }
            }
            return {
                ...styles,
                backgroundColor: isFocused ? COLORS.ORANGE : backgroundColor,
                color,
                fontSize: 14,
                padding: "0 4px 0 4px",
                borderLeft: borderLeft ? borderLeft : "",
                border: isFocused ? "1px solid black !important" : "",
                cursor: "pointer",
                direction: "ltr",
            };
        },
    };

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize() {
        this.setState({ screenWidth: window.innerWidth });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.defaultDistributor !== this.props.defaultDistributor) {
            const nextValue = this.props.defaultDistributor?._id || "";
            // Only sync when selected id actually changes – don't overwrite full menu label (with brackets/eta) with short getSelectedLabel after async vehicleLetter enrichment
            if (this.state.selectedDistributor.value !== nextValue) {
                const nextLabel = this.getSelectedLabel(this.props.defaultDistributor);
                this.setState({
                    selectedDistributor: { label: nextLabel, value: nextValue },
                });
            }
        }
    }

    getMenuPosition() {
        if (this.selectRef?.select?.controlRef) {
            const controlEl = this.selectRef.select.controlRef;
            const rect = controlEl.getBoundingClientRect();

            // Get the actual menu width after render
            const menuEl = document.querySelector("[class*=\"-menu\"]");
            const menuWidth = menuEl ? menuEl.offsetWidth : rect.width;

            const leftOffset = (rect.width - menuWidth) / 2;

            return {
                left: `${leftOffset}px`,
                right: "auto",
            };
        }
        return {};
    }

    shouldComponentUpdate(nextProps, nextState) {
        // re-render when menu visibility changes or when underlying data updates
        if (this.props.menuIsOpen !== nextProps.menuIsOpen) return true;
        if (this.props.distributors !== nextProps.distributors) return true;
        if (this.props.defaultDistributor !== nextProps.defaultDistributor) return true;
        if (this.props.orders !== nextProps.orders) return true;
        if (this.state.nearestList !== nextState.nearestList) return true;
        return false;
    }

    getNearestList(cb = {}) {
        this.props.getNearestDistributorList((data) => {

            let filteredData = Array.isArray(data) ? data : [];

            if (this.props.order && this.props.order.paymentType == 7) {
                filteredData = filteredData.filter((item) => item.posTerminalId);
            }

            this.setState({
                nearestList: filteredData,
            });

            cb();
        });
    }

    getOrderData(distributor, convert) {
        const orderData = {
            eta: "",
            address: "",
        };
        this.props.orders.map((order) => {
            if (order.distributor && order.distributor === distributor) {
                if (convert) {
                    const separator = orderData.eta ? "," : "";
                    orderData.eta += `${separator} ${this.convertEtaToHours(
                        order.eta,
                    )}`;
                    orderData.address = order.receiver.address.substring(0, 70);
                } else {
                    orderData.eta = order.eta;
                }
            }
            return order;
        });
        return orderData;
    }

    convertEtaToHours(eta) {
        if (eta === null) {
            return "";
        }
        let hours = Math.trunc(eta / 60),
            minutes = Math.abs(eta % 60);
        if (hours) {
            if (Math.abs(hours) <= 9) {
                if (hours < 0) {
                    hours = `-0${Math.abs(hours)}`;
                } else {
                    hours = `0${hours}`;
                }
            }
            if (minutes <= 9) {
                minutes = `0${minutes}`;
            }
            return `${hours}:${minutes}`;
        }
        return eta;
    }

    checkNearest(name) {
        if (
            this.state.nearestList[this.props.order.orderId].find(
                (e) => e.id === name,
            )
        ) {
            return true;
        } else {
            return false;
        }
    }

    sortDistributors() {
        const distributors = this.props.distributors;
        distributors.sort((a, b) => {
            if (
                this.props.order &&
                this.state.nearestList &&
                Object.keys(this.state.nearestList).length &&
                this.state.nearestList[this.props.order.orderId]
            ) {
                const nearest1 = this.checkNearest(a.name);
                const nearest2 = this.checkNearest(b.name);
                const res = nearest2 - nearest1;
                if (res) {
                    return res;
                }
            }
            const status1 = DISTRIBUTOR_STATUSES.isAvailable(a.status);
            const status2 = DISTRIBUTOR_STATUSES.isAvailable(b.status);
            const result = status2 - status1;
            if (result) {
                return result;
            }
            let activity1 =
                    this.props.activity.find((e) => e.distributorId == a._id) ||
                    null,
                activity2 =
                    this.props.activity.find((e) => e.distributorId == b._id) ||
                    null;

            if (a.status !== b.status) {
                if (
                    a.status === DISTRIBUTOR_STATUSES.UNAVAILABLE.statusId ||
                    a.status === DISTRIBUTOR_STATUSES.IS_OFFLINE.statusId
                ) {
                    return 1;
                }
                if (
                    b.status === DISTRIBUTOR_STATUSES.UNAVAILABLE.statusId ||
                    b.status === DISTRIBUTOR_STATUSES.IS_OFFLINE.statusId
                ) {
                    return -1;
                }
            }
            if (activity1 || activity2) {
                if (!activity1) {
                    return 1;
                } else if (!activity2) {
                    return -1;
                } else {
                    return (
                        (activity1.uniqueOrdersCount + 1) /
                        convertEtaToMinutes(activity1.online) -
                        (activity2.uniqueOrdersCount + 1) /
                        convertEtaToMinutes(activity2.online)
                    );
                }
            }

            let eta1 = Number(this.getOrderData(a._id).eta) || 0,
                eta2 = Number(this.getOrderData(b._id).eta) || 0;

            return eta1 - eta2 || a._id - b._id;
        });
        return distributors;
    }

    getClassBg(distributor) {
        let classBg =
            distributor.status === DISTRIBUTOR_STATUSES.AVAILABLE.statusId
                ? DISTRIBUTOR_STATUSES.AVAILABLE.classBg
                : "";
        if (this.props.fromMap) {
            classBg = DISTRIBUTOR_STATUSES.statusById(distributor.status)
                .classBg;
        }
        const { order, showAll } = this.props;

        let lockedForTable, lockedForMap;

        const isSanitaryDisabled = this.shouldExcludeDueToSanitaryBook(distributor);

        lockedForTable = (distributor.locked &&
                !this.props.fromMap &&
                (order?.paymentType === 3 || order?.paymentType === 7)) ||
            isSanitaryDisabled;

        lockedForMap = distributor.locked && showAll;

        if (lockedForTable || lockedForMap) {
            classBg = `${DISTRIBUTOR_STATUSES.UNAVAILABLE.classBg} text-red`;
        }
        return classBg;
    }

    onShowHandler = (e) => {
        if (typeof this.props.onClick === "function") {
            this.props.onClick(e);
        }
        // Activate row when select is focused
        if (typeof this.props.onRowActivate === "function") {
            this.props.onRowActivate();
        }
        this.getNearestList(() => {
            this.distributors = this.sortDistributors();
            setTimeout(() => {
                this.forceUpdate();
            }, 1000);
        });
    };

    onMenuOpenHandler = () => {
        const placement = this.getMenuPlacement();

        this.setState({
            menuPlacement: placement,
        });

        if (!this.menuIsOpen) {
            this.menuIsOpen = true;

            if (typeof this.props.onMenuOpen === "function") {
                this.props.onMenuOpen();
            }

            if (typeof this.props.onRowActivate === "function") {
                this.props.onRowActivate();
            }
        }
    };

    onSelectChange = (value) => {
        this.menuIsOpen = false;
        this.setState({ selectedDistributor: value });
        this.forceUpdate();
    };

    checkWorkingTime = (workingTimes, now) => {
        if (!workingTimes || !workingTimes.length) {
            return false;
        }
        let result = false;
        workingTimes.forEach((e) => {
            let start = moment(e.start, "HH:mm");
            let end = moment(e.end, "HH:mm");
            if (now.isBetween(start, end)) {
                result = true;
            }
        });
        return result;
    };

    getOptions = () => {
        const {
            defaultDistributor,
            additionalList = [],
        } = this.props;

        if (this.distributors.length === 0) {
            this.distributors = this.sortDistributors();
        }

        const distributorTypedList = this.initializeDistributorTypeList();
        const distributors = [...additionalList, ...this.distributors];
        const timeNow = moment();

        this.processDefaultDistributor(distributorTypedList[0], defaultDistributor, distributorTypedList);

        distributors.forEach(distributor => {
            if (this.shouldIncludeDistributor(distributor, defaultDistributor)) {
                this.processDistributor(distributor, distributorTypedList, timeNow);
            }
        });

        return this.getFinalDistributorList(distributorTypedList);
    };

    initializeDistributorTypeList = () => [
        {
            label: "selected",
            options: [],
        },
        {
            label: translator.translate("_car_"),
            options: [],
        },
        {
            label: translator.translate("_moto_bike_"),
            options: [],
        },
        {
            label: translator.translate("_scooter_"),
            options: [],
        },
        {
            label: translator.translate("_bike_"),
            options: [],
        },
        {
            label: translator.translate("_pedestrian_"),
            options: [],
        },
        {
            label: translator.translate("_pedestrian_"),
            options: [],
        },
        {
            label: translator.translate("_pedestrian_"),
            options: [],
        },
    ];

    processDefaultDistributor = (selectedCategory, defaultDistributor, distributorTypedList) => {
        if (!defaultDistributor) return;

        const existingValues = new Set(selectedCategory.options.map(option => option.value));

        if (!existingValues.has("")) {
            selectedCategory.options.push({
                value: "",
                label: translator.translate("_choose_"),
                className: "",
                isDisabled: false,
            });
            existingValues.add("");
        }

        const defaultOrderData = this.getOrderData(defaultDistributor._id, true);
        const defaultEta = defaultOrderData.eta;
        const defaultAddress = defaultOrderData.address;
        const defaultOrderCount = defaultDistributor.orderCount ? `${defaultDistributor.orderCount} ` : "";
        const defaultPosMarker = this.getPosTerminalMarker(defaultDistributor);
        const defaultBrackets = this.formatBrackets(defaultOrderCount, defaultPosMarker);

        if (!existingValues.has(defaultDistributor._id)) {
            const letter = defaultDistributor.vehicleLetter;
            const rawName = defaultDistributor.name || "";
            const hasPrefixedName = /^\([^)]+\)\s+/.test(rawName);
            const effectiveLetter = letter || (hasPrefixedName ? rawName.match(/^\(([^)]+)\)/)?.[1] : "");
            const displayName = hasPrefixedName && !letter ? rawName.replace(/^\([^)]+\)\s+/, "") : rawName;
            const payableAmount = defaultDistributor.payableAmount ? `${defaultDistributor.payableAmount} ` : "";
            const suffix = `${defaultBrackets ? ` ${defaultBrackets}` : ""}${defaultEta ? ` ${defaultEta}` : ""}${defaultAddress ? ` ${defaultAddress}` : ""}`;
            const label = effectiveLetter
                ? `${payableAmount} ${effectiveLetter} ${displayName}${suffix}`.trim()
                : `${payableAmount}${displayName}${suffix}`.trim();
            selectedCategory.options.push({
                value: defaultDistributor._id,
                label,
                shortLabel: effectiveLetter
                    ? `${payableAmount} ${effectiveLetter} ${displayName} ${defaultBrackets}`.trim()
                    : `${payableAmount}${displayName} ${defaultBrackets}`.trim(),
                className: "",
            });
        }
    };

    shouldIncludeDistributor = (distributor, defaultDistributor) => {
        const { order, showAll } = this.props;

        if (this.shouldExcludeDueToPosTerminal(distributor)) {
            return false;
        }

        // sanitary: do not remove from list, handled as disabled in processDistributor

        if (distributor.isDeactive) {
            return false;
        }

        if (!showAll && this.isDistributorUnavailable(distributor)) {
            return false;
        }

        if (defaultDistributor?._id === distributor._id) {
            return false;
        }

        return !this.shouldExcludeDueToPartnerLoan(distributor);
    };

    shouldExcludeDueToSanitaryBook = (distributor) => {
        const sanitaryBookStatus = distributor?.hasActiveSanitaryBook;

        if (sanitaryBookStatus) return false;

        const requiresSanitaryBook = this.props.order?.requiresSanitaryBook;
        return !!requiresSanitaryBook;
    };

    shouldExcludeDueToPosTerminal = (distributor) => {
        const { order } = this.props;

        const isPosPayment = order?.paymentType == PAYMENT_TYPES.POS_TERMINAL.id;
        const hasNoTerminal = !distributor.posTerminalId;

        return isPosPayment && hasNoTerminal;
    };

    isDistributorUnavailable = (distributor) => {
        const unavailableStatuses = [
            DISTRIBUTOR_STATUSES.IS_OFFLINE.statusId,
            DISTRIBUTOR_STATUSES.UNAVAILABLE.statusId,
        ];

        return unavailableStatuses.includes(distributor.status);
    };

    shouldExcludeDueToPartnerLoan = (distributor) => {
        const { order } = this.props;

        const isPartner = distributor.distributorCooperationType ===
            DISTRIBUTOR_COOPERATION_TYPES.partner.id;
        const isLoanPayment = order?.paymentType === PAYMENT_TYPES.LOAN.id;

        return isPartner && isLoanPayment;
    };

    processDistributor = (distributor, distributorTypedList, timeNow) => {
        const orderCount = distributor.orderCount ? `${distributor.orderCount} ` : "";
        const orderData = this.getOrderData(distributor._id, true);

        const { order } = this.props;

        let classBg = this.getClassBg(distributor);

        const isSanitaryDisabled = this.shouldExcludeDueToSanitaryBook(distributor);

        const isDisabled =
            (distributor.locked &&
                !this.props.fromMap &&
                (order?.paymentType === 3 || order?.paymentType === 7)) ||
            isSanitaryDisabled;

        const isWorkingTimeActive = this.checkWorkingTime(distributor.workingTime, timeNow) &&
            !orderData.address;

        if (isWorkingTimeActive) {
            classBg += " border-left-active";
        }

        const posTerminalMarker = this.getPosTerminalMarker(distributor);
        const brackets = this.formatBrackets(orderCount, posTerminalMarker);

        const option = {
            value: distributor._id,
            label: this.formatDistributorLabel(distributor, brackets, orderData),
            shortLabel: this.formatDistributorShortLabel(distributor, brackets),
            className: classBg?.trim(),
            isDisabled,
        };

        const categoryIndex = Number(distributor.type) || 0;

        const isEligibleDistributor = (distributor, from) => {
            if (distributor?._id === "") return false;

            return !!(distributor?.financeTariffId && distributor?.hasCertificate);
        };

        const category = distributorTypedList[categoryIndex];
        if (category && isEligibleDistributor(distributor, this.props.from)) {
            category.options.push(option);
        }
    };

    getPosTerminalMarker = (distributor) => {
        if (!distributor.posTerminalId) return "";

        return distributor.posTerminalType == DISTRIBUTOR_TERMINAL_TYPES.standard.id
            ? "P"
            : "S";
    };

    formatBrackets = (orderCount, posTerminal) => {
        if (!orderCount && !posTerminal) return "";

        return `(${orderCount}${posTerminal})`;
    };

    formatDistributorLabel = (distributor, brackets, orderData) => {
        const payableAmount = distributor.payableAmount ? `${distributor.payableAmount} ` : "";
        const letter = distributor.vehicleLetter;
        const name = distributor.name || "";
        const hasPrefixedName = /^\([^)]+\)\s+/.test(name);
        const effectiveLetter = letter || (hasPrefixedName ? name.match(/^\(([^)]+)\)/)?.[1] : "");
        const displayName = hasPrefixedName && !letter ? name.replace(/^\([^)]+\)\s+/, "") : name;
        const suffix = `${brackets ? ` ${brackets}` : ""}${orderData.eta ? ` ${orderData.eta}` : ""}${orderData.address ? ` ${orderData.address}` : ""}`;
        if (effectiveLetter) {
            return `${payableAmount} ${effectiveLetter} ${displayName}${suffix}`.trim();
        }
        return `${payableAmount}${displayName}${suffix}`.trim();
    };

    formatDistributorShortLabel = (distributor, brackets) => {
        const payableAmount = distributor.payableAmount ? `${distributor.payableAmount} ` : "";
        const letter = distributor.vehicleLetter;
        const name = distributor.name || "";
        const hasPrefixedName = /^\([^)]+\)\s+/.test(name);
        const effectiveLetter = letter || (hasPrefixedName ? name.match(/^\(([^)]+)\)/)?.[1] : "");
        const displayName = hasPrefixedName && !letter ? name.replace(/^\([^)]+\)\s+/, "") : name;
        if (effectiveLetter) {
            return `${payableAmount} ${effectiveLetter} ${displayName} ${brackets}`.trim();
        }
        return `${payableAmount}${displayName} ${brackets}`.trim();
    };

    getFinalDistributorList = (distributorTypedList) => [
        distributorTypedList[0], // selected
        distributorTypedList[1], // car
        distributorTypedList[2], // bike
        distributorTypedList[4], // scooter
        distributorTypedList[3], // moto_bike
        distributorTypedList[5], // pedestrian
    ];

    formatGroupLabel = (data) => {
        const groupStyles = {
            marginTop: 0,
            marginBottom: "5px",
            width: auto,
            backgroundColor: "black",
            display: "flex",
            minHeight: "2px",
        };

        if (data.label === "selected") {
            return null;
        }

        return <div style={groupStyles}></div>;
    };

    render() {
        const { selectedDistributor, screenWidth } = this.state;
        const isMobile = screenWidth <= 768;

        return (
            <Select
                ref={this.selectRef}
                isDisabled={this.props.isDisabled}
                menuIsOpen={this.props.menuIsOpen && this.menuIsOpen}
                onMenuOpen={this.onMenuOpenHandler}
                onMenuClose={() => {
                    if ("function" === typeof this.props.onMenuClose) {
                        this.props.onMenuClose();
                    }
                    this.menuIsOpen = false;
                }}
                onFocus={this.onShowHandler}
                onClick={this.props.onClick}
                onChange={(e) => {
                    if ("function" === typeof this.props.onSelectChange) {
                        this.props.onSelectChange();
                    }
                    if ("function" === typeof this.props.onChange) {
                        this.props.onChange(e);
                    }
                    this.onSelectChange(e);
                }}
                styles={this.selectStyles}
                menuPortalTarget={this.props.inModal ? null : document.body}
                menuPosition="fixed"
                menuPlacement={
                    isMobile
                        ? "bottom"
                        : this.state.menuPlacement
                }
                options={this.getOptions()}
                formatGroupLabel={this.formatGroupLabel}
                formatOptionLabel={this.formatOptionLabel}
                filterOption={(option, input) => {
                    if (!input) return true;
                    const label = option.label || "";
                    return label.toLowerCase().includes(input.toLowerCase());
                }}
                className={this.props.className || ""}
                placeholder={translator.translate("_search_")}
                noOptionsMessage={() =>
                    translator.translate("_no_restaurant_matches_")
                }
                value={selectedDistributor}
                isSearchable={true}
            />
        );
    }
}

const getActivity = (state) => {
    if (!state.quantitativeMovements.activityList) {
        return [];
    }
    return state.quantitativeMovements.activityList.map((e) => {
        let mouvment = state.quantitativeMovements.list.find(
            (item) => item.distributorId == e.id,
        );
        if (e.online.split(":").length > 2) {
            e.online = e.online.slice(0, -3);
        }
        if (mouvment) {
            return { ...e, ...mouvment };
        } else {
            return { ...e, uniqueOrdersCount: 0 };
        }
    });
};

let mapStateToProps = (state) => {
    return {
        orders: state.orders,
        activity: getActivity(state),
    };
};

export default connect(mapStateToProps, { getNearestDistributorList })(
    SelectList,
);
